<template>
  <div v-if="product" class="mt-4 lg:mt-6">
    <component
      :is="isOutOfStock ? 'div' : BaseSticky"
      name="productDetailsCta"
      :stick-above-element="stickAboveElement"
    >
      <div ref="productDetailsCtaRef" class="f-col gap-2">
        <vf-button
          v-if="(!isComingSoon && !isSignInToBuy && !isRobisOnly) && nextAttribute && !product.allVariantsOutOfStock"
          :class-content="{ 'w-full between': $feature.showPriceInCtas }"
          :size="ctaSize"
          @click="handleNextAttribute"
        >
          {{ $t.selectSize }}
          <product-pricing
            v-if="$feature.showPriceInCtas"
            class-current="c-white"
            :currency="product.currency"
            hide-strikethrough-price
            :price="product.price"
          />
        </vf-button>
        <product-notify-me
          v-else-if="isNotifyMe && ((selectedVariant && isSelectedProductOutOfStock)
            || isComingSoon || product.allVariantsOutOfStock)"
          :size="ctaSize"
        />
        <vf-button
          v-else-if="isComingSoon"
          disabled
          :size="ctaSize"
        >
          {{ comingSoonText }}
        </vf-button>
        <template v-else-if="!isComingSoon">
          <vf-button
            v-if="!isProductOrInventoryPending && isOutOfStock && !isSignInToBuy"
            disabled
            :size="ctaSize"
          >
            {{ $t.outOfStock }}
          </vf-button>
          <vf-button
            v-else-if="isRobisOnly"
            :size="ctaSize"
            @click="handleReserveInStore"
          >
            {{ $t.reserveInStore }}
          </vf-button>
          <template v-else>
            <vf-button
              id="pdp-add-to-cart"
              aria-live="polite"
              :class-content="{ 'w-full between': $feature.showPriceInCtas }"
              :disabled="!isProductOrInventoryPending && (addToCartLoading || checkoutCtaLoading)"
              :loading="isProductOrInventoryPending || (addToCartLoading && !checkoutCtaLoading)"
              :size="ctaSize"
              type="submit"
              @click="handleAddToCart"
            >
              {{ isSignInToBuy ? $t.signInToBuy : $t.addToCart }}
              <product-pricing
                v-if="$feature.showPriceInCtas"
                class-current="c-white"
                :currency="product.currency"
                hide-strikethrough-price
                :price="product.price"
              />
            </vf-button>
          </template>
        </template>
        <template v-if="!isComingSoon && !isRobisOnly">
          <client-only>
            <checkout-apple-pay-button v-if="isApplePayEnabled" class="<lg:mt-2" @click="handleApplePay" />
            <lazy-paypal-payments
              v-if="!isDigitalProduct && $feature.showPaypalOnPDP && !auth.isEmployee && product && productPrice"
              :amount="productPrice.lowCurrent"
              :class="brandClasses.paypalPayments"
              placement="product"
            />
          </client-only>
          <base-button
            v-if="$feature.allowProductReservationOnPDP"
            class="w-full flex gap-2 bg-grey-90 px-2 py-4 underlined"
            :size="ctaSize"
            :variant="ctaVariant"
            @click="handleReserveInStore"
          >
            <vf-icon size="md" src="/icons/vans-bag.svg" />
            {{ $t.reserveInStore }}
          </base-button>
          <base-button
            v-else-if="$feature.showFindInStoreOnPDP"
            class="text-center underlined <lg:b-y <lg:b-grey-70 <lg:bg-grey-90 <lg:px-4 <lg:py-3 <lg:-mx-4 lg:py-2"
            :size="ctaSize"
            :variant="ctaVariant"
            @click="handleFindInStore"
          >
            {{ $t.findInStore }}
          </base-button>
        </template>
      </div>
    </component>
  </div>
</template>

<script lang="ts" setup>
import { BaseSticky } from '#components'

const { brandClasses, details: { ctaSize, ctaVariant }, stickAboveElement } = useAppConfig().pages.pdp

const { $t, $feature } = useNuxtApp()
const {
  allowApplePayOrderOnPDP,
  allowApplePayOrderOnPDPWithBopis
} = useExpressPaymentFeatures()

const auth = useAuthStore()
const cart = useCartStore()
const { DialogSignIn } = useDialogsStore()
const {
  addToCart,
  addToCartLoading,
  attributeSelectionLabels,
  checkoutCtaLoading,
  findInStore,
  handleNextAttribute,
  isComingSoon,
  isDigitalProduct,
  isNotifyMe,
  isOutOfStock,
  isProductOrInventoryPending,
  isRobisOnly,
  isSelectedProductOutOfStock,
  isSignInToBuy,
  nextAttribute,
  product,
  productPrice,
  selectedVariant,
  shippingMethod,
  triggerApplePaySession
} = usePdp()
const productDetailsCtaRef = useState<HTMLElement>('productDetailsCtaRef')
const { pending: isCartPending } = storeToRefs(cart)

const isApplePayEnabled = computed(() =>
  allowApplePayOrderOnPDP.value
  && window.ApplePaySession
  && (allowApplePayOrderOnPDPWithBopis.value || shippingMethod.value !== 'pickup')
)

const comingSoonText = computed(() =>
  product.value?.targetPublishDate
    ? `${$t.comingSoon} – ${formatTargetPublishDate(
      product.value?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )}`
    : $t.comingSoon
)

const handleSignInToBuy = async () => {
  if (isSignInToBuy.value) {
    await DialogSignIn.open({
      formLocation: 'modal:single:pdp:none',
      isSignInToBuy: true
    })
    if (auth.loggedIn) {
      await until(isCartPending).toBe(false)
      return handleSignInToBuy()
    }
    throw new Error('User not signed in')
  }
}

const handleApplePay = async () => {
  try {
    await handleSignInToBuy()
    handleNextAttribute({ enableApplePay: true }, () => triggerApplePaySession())
  }
  catch (e) {
    console.info(e)
  }
}

const handleFindInStore = () => {
  try {
    handleNextAttribute({ enableFindInStore: true }, () => findInStore(attributeSelectionLabels.value, 'pickup'))
  }
  catch (e) {
    console.info(e)
  }
}

const handleReserveInStore = () => {
  try {
    handleNextAttribute({ enableReserveInStore: true }, () => findInStore(attributeSelectionLabels.value, 'reserve'))
  }
  catch (e) {
    console.info(e)
  }
}

const handleAddToCart = async () => {
  try {
    await handleSignInToBuy()
    handleNextAttribute({}, async () => await addToCart())
  }
  catch (e) {
    console.info(e)
  }
}
</script>
